<template>
  <teleport to="body">
    <WdsModal :show="open" :title="$t('Supplier employment confirmation')" @cancel="close">
      <WdsStack direction-column gap-md>
        <WdsText>
          {{
            $t(
              'As the staffing agency for this hire, you are required to answer the following questions. By confirming, you accept responsibility for the accuracy of your answers.',
            )
          }}
        </WdsText>

        <WdsStack
          v-for="(question, index) in questions"
          :key="index"
          direction-column
          gap-sm
          class="tw-p-3 tw-border tw-border-gray-200 tw-rounded-lg"
        >
          <WdsText>{{ question.label }}</WdsText>
          <WdsRadioGroup
            :checked="answers[index] ?? undefined"
            :name="`nl-supplier-question-${index}`"
            @update:checked="setAnswer(index, String($event))"
          >
            <WdsRadio :value="'yes'">{{ $t('Yes') }}</WdsRadio>
            <WdsRadio :value="'no'">{{ $t('No') }}</WdsRadio>
          </WdsRadioGroup>
        </WdsStack>
      </WdsStack>

      <template v-if="!hideFooter" #footer>
        <WdsStack justify-end align-center gap-sm class="tw-w-full">
          <WdsButton tertiary @click="close">
            {{ $t('Cancel') }}
          </WdsButton>

          <WdsButton success :disabled="!allAnswered || isSubmitting" @click="handleConfirm">
            {{ isSubmitting ? $t('Confirming...') : $t('Confirm') }}
          </WdsButton>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import {
  WdsButton,
  WdsModal,
  WdsRadio,
  WdsRadioGroup,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import queriesToRefetch from '../../queriesToRefetch'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { ComplianceName } from '@/gql/graphql'
import { useToast } from '@/composables/useToast'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import type { ComplianceModalProps } from '@/components/Hire/HirePageCompliance/modals/complianceModals'

const toast = useToast()

interface SavedAnswerField {
  __typename?: string | null
  label?: string | null
  value?: string | null
}

const props = withDefaults(
  defineProps<ComplianceModalProps & { prefilledAnswers?: SavedAnswerField[] | null }>(),
  {
    open: false,
    hideFooter: false,
    prefilledAnswers: null,
  },
)

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

const isSubmitting = ref(false)

const { mutate: updateCompliance } = useMutation(UpdateComplianceMutation)

// `key` is the stable English id stored with each answer and matched when reopening; `label` is the
// translated text shown on screen. Keep `key` stable even if the wording changes.
const questions = [
  {
    key: 'Is the employee employed by you on the basis of an employment contract concluded with you under Dutch law, and are the activities primarily carried out in the Netherlands?',
    label: $t(
      'Is the employee employed by you on the basis of an employment contract concluded with you under Dutch law, and are the activities primarily carried out in the Netherlands?',
    ),
  },
  {
    key: 'Do you have an employment contract with the employee (not being a payroll agreement)?',
    label: $t(
      'Do you have an employment contract with the employee (not being a payroll agreement)?',
    ),
  },
  {
    key: 'Is the employee a highly skilled migrant?',
    label: $t('Is the employee a highly skilled migrant?'),
  },
  {
    key: 'Did you (or someone from your organization) perform the initial recruitment and selection (allocation) of the (candidate) employee?',
    label: $t(
      'Did you (or someone from your organization) perform the initial recruitment and selection (allocation) of the (candidate) employee?',
    ),
  },
  {
    key: 'Is the employee working exclusively for the current client during the term of the employment contract?',
    label: $t(
      'Is the employee working exclusively for the current client during the term of the employment contract?',
    ),
  },
]

const answers = ref<(string | null)[]>(questions.map(() => null))

const allAnswered = computed(() => answers.value.every((a) => a !== null))

const setAnswer = (index: number, value: string | null) => {
  answers.value = answers.value.map((current, i) => (i === index ? value : current))
}

// Paint saved answers from the parent. Radios use lowercase yes/no.
const mapAnswersFromFields = (fields: readonly SavedAnswerField[]) => {
  answers.value = questions.map((question) => {
    const match = fields.find(
      (field) => field.__typename === 'ComplianceDataField' && field.label === question.key,
    )
    if (match?.__typename !== 'ComplianceDataField') return null
    const value = match.value?.toLowerCase()
    return value === 'yes' || value === 'no' ? value : null
  })
}

watch(
  () => props.prefilledAnswers,
  (data) => {
    if (data === null) return
    mapAnswersFromFields(data)
  },
  { immediate: true },
)

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      isSubmitting.value = false
      if (props.prefilledAnswers) {
        mapAnswersFromFields(props.prefilledAnswers)
      } else {
        answers.value = questions.map(() => null)
      }
    }
  },
)

const close = () => {
  emit('update:open', false)
}

const handleConfirm = async () => {
  isSubmitting.value = true

  try {
    const submissionAnswers = questions.map((q, i) => ({
      question: q.key,
      answer: answers.value[i],
    }))

    await updateCompliance({
      input: {
        name: ComplianceName.NlSupplierEmploymentConfirmation,
        data: JSON.stringify({
          answers: submissionAnswers,
        }),
        hireId: props.hireId,
      },
    })

    await publicApi.refetchQueries({
      include: queriesToRefetch,
    })

    close()
    toast.success($t('Supplier employment confirmation completed'))
  } catch (error) {
    toast.error($t('An error occurred, please try again.'))
  } finally {
    isSubmitting.value = false
  }
}
</script>
